import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isMobile } from "react-device-detect";
import styled, { css } from "styled-components";

import breakpoints from "_constants/breakpoints";
import { currencyFormatter } from "_helpers";
import { customerActions, constantActions } from "_store/actions";

import { CancelSubscriptionConfirm } from "components/Modals";

import Table from "containers/Table/Table";

import { CancelButton } from "widget/Buttons";
import { SpinnerWrapper as Spinner } from "widget/Spinner";

import SubscriptionsMobile from "./Mobile";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: left;
  background-color: #ffffff;
  width: 100%;
  max-width: 1360px;
  padding: 32px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 48px;
  animation: 0.2s linear fadeIn;
  @keyframes fadeIn {
    from {
      opacity: 0%;
    }
    to {
      opacity: 100%;
    }
  }

  ${({ theme: { mobile } }) =>
    !mobile &&
    css`
      box-shadow: 0px 3px 25px rgba(168, 168, 168, 0.3);

      @media (max-width: 1350px) {
        width: 90%;
      }
    `}

  @media (max-width: ${breakpoints.mobileM}) {
    padding: 16px 16px 24px;
  }
`;
const Title = styled.div`
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  text-align: left;
  color: #03589c;
  margin-bottom: 32px;

  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      font-size: 22px;
      text-align: center;
      border-bottom: none;

      @media (max-width: ${breakpoints.mobileM}) {
        font-size: 18px;
        padding: 0;
        height: 64px;
        margin-bottom: 18px;
      }
    `}
`;
const InfoForm = styled.div`
  ${({ theme: { mobile } }) =>
    mobile
      ? css`
          @media (max-width: ${breakpoints.mobileM}) {
            padding: 0px;
          }
        `
      : css`
          @media (max-width: 700px) {
            /* padding: 48px 0 0 16px; */
          }
        `}
`;
const EmptyMessage = styled.div`
  color: #7b848d;
  font-weight: bold;
  text-align: center;
`;

export default function Subscriptions() {
  const dispatch = useDispatch();

  const { subscriptions, subscriptionsLoading } = useSelector(({ customer }) => customer);
  const { ordersList, ordersListLoading } = useSelector(({ orders }) => orders);
  const [open, setOpen] = useState(false);
  const [cancelInfo, setCancelInfo] = useState(null);

  const customerProducts = useMemo(() => {
    return (
      ordersList?.reduce((acc, order) => {
        if (!order?.products) return acc;

        return [...acc, ...order.products];
      }, []) || []
    );
  }, [ordersList]);

  const filteredSubscriptions = useMemo(
    () =>
      subscriptions.filter((item) => {
        return customerProducts.some((product) => product?.id === item?.productId);
      }),
    [subscriptions, customerProducts]
  );

  const handleCancelSubscription = ({ id, productId, productName }) => {
    setOpen(true);
    setCancelInfo({ id, productId, productName });
  };

  // const handleCancelSubscriptionConfirm = () => {
  //   setOpen(false);
  //   dispatch(customerActions.cancelSubscription(cancelInfo.id, cancelInfo.productId, cancelInfo.productName));
  // };

  const handleCancelSubscriptionCancel = () => {
    setOpen(false);
    setCancelInfo(null);
  };

  const COLUMNS = [
    {
      label: "Order ID",
      key: "orderId",
      width: 160,
      getlink: (rowProps) => (rowProps?.id ? `/orders/${rowProps?.id}` : "#"),
    },
    {
      label: "Product Name",
      key: "productName",
      adaptive: true,
      bold: true,
      getlink: (rowProps) => (rowProps?.id && rowProps?.productId ? `${rowProps?.id}/${rowProps?.productId}` : "#"),
    },
    { label: "Price", key: "price", priceFormatter: currencyFormatter, width: 120 },
    { label: "Next Billing Date", key: "nextPayDate", format: "MMM DD, YYYY", width: 180 },
    { label: "Status", key: "status", width: 160 },
    { label: "Cancel", key: "", width: 180, element: CancelButton, optional: true, onClick: handleCancelSubscription },
  ];

  useEffect(() => {
    dispatch(customerActions.getSubscriptions());
    dispatch(constantActions.getProductFileTypes(["TRADEMARK_Privacy_Package"]));
  }, [dispatch]);

  return (
    <>
      {subscriptionsLoading || ordersListLoading ? (
        <Spinner />
      ) : (
        <Container>
          <Title>My Subscriptions</Title>
          <InfoForm>
            {filteredSubscriptions?.length ? (
              <>
                {isMobile ? (
                  <SubscriptionsMobile subscriptions={filteredSubscriptions} onCancel={handleCancelSubscription} />
                ) : (
                  <Table data={filteredSubscriptions} columns={COLUMNS} contentMaxHeight="540px" minWidth="1000px" />
                )}
              </>
            ) : (
              <EmptyMessage>No Subscriptions for this account</EmptyMessage>
            )}
          </InfoForm>

          {open && (
            <CancelSubscriptionConfirm
              cancelInfo={cancelInfo}
              ordersList={ordersList}
              // onConfirm={handleCancelSubscriptionConfirm}
              onCancel={handleCancelSubscriptionCancel}
            />
          )}
        </Container>
      )}
    </>
  );
}
